import { h, Component } from 'preact';
import s from 'components/shared/theaters/ticketing/ticketing.sss';
import Store from 'store/store';
import * as actions from 'store/actions';
import * as ticketingStates from 'store/updateTicketing';
import { accessNested, joinClasses, textToJsxArray } from 'utils/utils';
import { track } from 'utils/tracking';
import { getCopy } from 'utils/copy';
import TicketTypes from 'components/shared/theaters/ticketing/ticketTypes/ticketTypes';
import TicketSeating from 'components/shared/theaters/ticketing/ticketSeating/ticketSeating';
import TicketReview from 'components/shared/theaters/ticketing/ticketReview/ticketReview';
import TicketContactDetails from 'components/shared/theaters/ticketing/ticketContactDetails/ticketContactDetails';
import PaymentGateway from 'components/shared/theaters/ticketing/gateways/paymentGateway';
import TicketComplete from 'components/shared/theaters/ticketing/ticketComplete/ticketComplete';
import TicketDetails from 'components/shared/theaters/ticketing/ticketDetails/ticketDetails';
import TicketCountdown from 'components/shared/theaters/ticketing/ticketCountdown/ticketCountdown';
import TicketError from 'components/shared/theaters/ticketing/ticketError/ticketError';


/**
 * Return whether ticketing details are shown based on the current ticketing step.
 *
 * @param {Object} ticketing - the ticketing state
 * @return {boolean} true if details are opened for the current step, false otherwise
 */
function isDetailsOpened(ticketing) {
	return ticketing.state === ticketingStates.REVIEW;
}


export default class Ticketing extends Component {

	///////////////////////
	//// LIFE CYCLE ///////
	///////////////////////

	constructor(props) {
		super(props);

		this.state = {
			ticketDetailsOpen: true,
			userInteracted: false
		};

		this.hasMessage = false;
		this.shouldScrollTop = false;
	}

	componentDidMount() {
		if (typeof document !== 'undefined') {
			// Disable scrolling and hide the footer
			document.body.classList.add('footerHidden', s.noscroll);
		}
	}

	componentWillReceiveProps(nextProps) {
		// Open/Close the detail menu for each step
		if (nextProps.ticketing.state !== this.props.ticketing.state) {
			this.setState({ ticketDetailsOpen: isDetailsOpened(nextProps.ticketing) });
			this.shouldScrollTop = true;
		}
	}

	componentDidUpdate() {
		// Hack to remove a rendering bug in Chrome
		// If you remove it, you will notice a 1px border on the right of the container sometimes
		// NOTE: 29-Nov-17: removed as causing bugs with the canvas seat picker component
		// const parent = this.base.parentElement.parentElement;
		// parent.style.width = parent.offsetWidth + 'px';
		// setTimeout(() => { parent.style.width = ''; }, 1);

		// Focus the OK button so you can close the box with ENTER
		if (this.hasMessage) {
			this.$okBtn.focus();
		}
		if (this.shouldScrollTop) {
			this.$steps.scrollTop = 0;
			this.shouldScrollTop = false;
		}
	}

	componentWillUnmount() {
		if (typeof document !== 'undefined') {
			// Re-enable scrolling and show the footer
			document.body.classList.remove('footerHidden', s.noscroll);
		}
	}

	///////////////////////
	//// CLICK HANDLERS ///
	///////////////////////

	closeMessage = () => {
		Store.emit(actions.CLEAR_TICKETING_MESSAGE);

		const callback = accessNested(this.props, 'ticketing.apiMessageCallback');
		if (callback && typeof callback === 'function') {
			callback();
		}
	}

	///////////////////////
	//// FUNCTIONS ////////
	///////////////////////

	toggleTicketDetails = () => {
		track({ a: this.state.ticketDetailsOpen ? 'ticketing-details-close' : 'ticketing-details-open' });
		this.setState({
			ticketDetailsOpen: !this.state.ticketDetailsOpen
		});
	}

	changeScreening = (screening) => {
		const eventData = { screening, theater: this.props.theater };
		Store.emit(actions.SELECT_SCREENING, eventData);
		Store.emit(actions.GO_TICKETING_TYPES, eventData);
	}

	///////////////////////
	//// RENDER METHODS ///
	///////////////////////

	render(props) {
		const {
			theater,   // Theater - the currently active theater
			ticketing, // Object - the ticketing state
			appData,   // Object - the app data
			client     // Object - the client info
		} = props;

		const screening = theater.activeScreening;
		let step = '';

		const stepProps = { ticketing, theater, client };
		const country = accessNested(theater, 'location.country') || '';
		const currencyCopy = getCopy(`CURRENCY_${country.toUpperCase()}`) ? `CURRENCY_${country.toUpperCase()}` : 'CURRENCY';

		switch (ticketing.state) {
			case ticketingStates.SELECT_TICKETS:
				step = <TicketTypes {...stepProps} currencyCopy={currencyCopy}/>;
				break;
			case ticketingStates.SELECT_SEATS:
				step = <TicketSeating {...stepProps} appData={appData} />;
				break;
			case ticketingStates.REVIEW:
				step = <TicketReview {...stepProps} currencyCopy={currencyCopy} />;
				break;
			case ticketingStates.CONTACT_DETAILS:
				step = <TicketContactDetails {...stepProps} currencyCopy={currencyCopy} />;
				break;
			case ticketingStates.PAYMENT:
				step = <PaymentGateway {...stepProps} appData={appData} client={client} currencyCopy={currencyCopy} />;
				break;
			case ticketingStates.COMPLETE:
				step = <TicketComplete {...stepProps} />; // what is this iframeSize??
				break;
			default:
				step = <TicketError {...stepProps} />;
		}

		this.hasMessage = false;

		let message = '';
		if (typeof ticketing.apiMessage === 'string' && ticketing.apiMessage.length > 0) {
			message = ticketing.apiMessage;
		}

		// The overlay is only shown when ticketing is loading or when there's a message for the user.
		// The message takes the priority over the loader.
		let overlayContent = null;
		if (message !== '') {
			this.hasMessage = true;
			overlayContent = (
				<div class={s.message}>
					<p>{textToJsxArray(message)}</p>
					<button type="button" onClick={this.closeMessage} tabindex="1" ref={$ref => this.$okBtn = $ref}>OK</button>
				</div>
			);
		} else if (ticketing.apiLoading) {
			overlayContent = (
				<div class={s.loadingContent}>
					<svg className={s.loadingIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
						<path d="M26.826 27.214a32.5 32.5 0 0 1 45.086-1.217" fill="none" stroke="#fff" stroke-width="10" />
					</svg>
				</div>
			);
		}

		// This basically prevents the seating map to be behind the bottom bar
		// let bottomBarHeight = 0;
		// if (ticketing.timeout) bottomBarHeight += 26;
		// if (ticketing.state !== ticketingStates.PAYMENT && ticketing.state !== ticketingStates.ERROR) bottomBarHeight += 60;
		// const stepsStyle = { marginBottom: bottomBarHeight + 'px' };

		return (
			<div class={s.container + (!!ticketing.timeout ? ' timeoutActive' : '')}>
				<TicketDetails
					screening={screening}
					ticketing={ticketing}
					currencyCopy={currencyCopy}
					toggleTicketDetails={this.toggleTicketDetails}
					open={this.state.ticketDetailsOpen}
				/>
				<div class={s.steps} ref={$ref => this.$steps = $ref}>
					{step}
					<TicketCountdown countdownTo={ticketing.timeout} />
					<div class={joinClasses(s.overlay, (overlayContent !== null) && s.active)}>
						{overlayContent}
					</div>
				</div>
			</div>
		);
	}

}
